import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Plus, 
  FolderOpen, 
  BookOpen,
  Beaker,
  AlertTriangle
} from 'lucide-react';
import permissionsService from '../../services/permissionsService';

const StudyBuilderMain = () => {
  const navigate = useNavigate();
  const [hasAccess, setHasAccess] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkPermission = async () => {
      try {
        const canAccess = await permissionsService.hasPermission(
          permissionsService.PERMISSIONS.ACCESS_STUDY_BUILDER
        );
        setHasAccess(canAccess);
      } catch (error) {
        console.error('Error checking study builder permission:', error);
        setHasAccess(false);
      } finally {
        setLoading(false);
      }
    };
    
    checkPermission();
  }, []);

  useEffect(() => {
    if (hasAccess === false) {
      const timer = setTimeout(() => {
        navigate('/studies', { 
          state: { 
            accessDenied: true, 
            message: 'You do not have permission to access the Study Builder.' 
          } 
        });
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [hasAccess, navigate]);

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  // Show access denied message (will be briefly shown before redirect)
  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md dark:shadow-gray-900/20 p-8 text-center max-w-md">
          <div className="flex justify-center mb-4">
            <div className="bg-red-100 p-3 rounded-full">
              <AlertTriangle className="h-8 w-8 text-red-600" />
            </div>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">Access Denied</h2>
          <p className="text-gray-600 dark:text-gray-300 mb-4">You do not have permission to access the Study Builder.</p>
          <button
            onClick={() => navigate('/studies')}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            Return to Studies
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white dark:bg-gray-800 rounded-xl shadow-md dark:shadow-gray-900/20 overflow-hidden"
        >
          {/* Header */}
          <div className="px-6 py-5 bg-primary-600 flex items-center">
            <Beaker className="w-6 h-6 text-white mr-3" />
            <h2 className="text-xl font-semibold text-white">Study Builder</h2>
          </div>

          {/* Content */}
          <div className="px-6 py-10 sm:px-10">
            <div className="max-w-3xl mx-auto">
              {/* Welcome Section */}
              <div className="text-center mb-12">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-50 text-primary-600 rounded-full mb-6">
                  <Beaker className="w-8 h-8" />
                </div>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                  Welcome to Study Builder
                </h1>
                <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
                  Create, manage, and analyze your research studies with ease and efficiency
                </p>
              </div>

              {/* Action Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Create New Study Card */}
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm dark:shadow-gray-900/20 border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md dark:hover:shadow-gray-900/20 transition-shadow duration-300">
                  <div className="flex flex-col h-full">
                    <div className="bg-primary-50 text-primary-600 p-3 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                      <Plus className="w-6 h-6" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">Create New Study</h3>
                    <p className="text-gray-600 dark:text-gray-300 mb-6 flex-grow">Design a new research study from scratch with customizable parameters</p>
                    <button
                      onClick={() => navigate('/studybuilder/create')}
                      className="mt-auto w-full py-3 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors duration-300 flex items-center justify-center"
                    >
                      <Plus className="w-5 h-5 mr-2" />
                      Create Study
                    </button>
                  </div>
                </div>

                {/* Assessment Builder Card */}
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm dark:shadow-gray-900/20 border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md dark:hover:shadow-gray-900/20 transition-shadow duration-300">
                  <div className="flex flex-col h-full">
                    <div className="bg-primary-50 text-primary-600 p-3 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                      <BookOpen className="w-6 h-6" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">Assessment Builder</h3>
                    <p className="text-gray-600 dark:text-gray-300 mb-6 flex-grow">Create and manage assessments with custom screens and questions</p>
                    <button
                      onClick={() => navigate('/assessment-builder')}
                      className="mt-auto w-full py-3 bg-white dark:bg-gray-800 text-primary-600 font-medium rounded-lg border border-primary-200 hover:bg-primary-50 transition-colors duration-300 flex items-center justify-center"
                    >
                      <BookOpen className="w-5 h-5 mr-2" />
                      Open Builder
                    </button>
                  </div>
                </div>

                {/* View Study Drafts Card */}
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm dark:shadow-gray-900/20 border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md dark:hover:shadow-gray-900/20 transition-shadow duration-300">
                  <div className="flex flex-col h-full">
                    <div className="bg-primary-50 text-primary-600 p-3 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                      <FolderOpen className="w-6 h-6" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">Study Drafts</h3>
                    <p className="text-gray-600 dark:text-gray-300 mb-6 flex-grow">Access and continue working on your saved study drafts</p>
                    <button
                      onClick={() => navigate('/studybuilder/drafts')}
                      className="mt-auto w-full py-3 bg-white dark:bg-gray-800 text-primary-600 font-medium rounded-lg border border-primary-200 hover:bg-primary-50 transition-colors duration-300 flex items-center justify-center"
                    >
                      <FolderOpen className="w-5 h-5 mr-2" />
                      View Drafts
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default StudyBuilderMain;